/**
 * Dictionary para i18n minimalista.
 *
 * Por ahora solo soporta "es" (default) y "en".
 * Para agregar más idiomas: añadir entrada en `DICTIONARY`.
 *
 * Uso:
 *   import { t } from "@/lib/i18n";
 *   <p>{t("dashboard.welcome")}</p>
 *
 * Para que un componente reaccione al cambio de idioma:
 *   import { useI18n } from "@/lib/i18n";
 *   const { lang, setLang } = useI18n();
 *   setLang("en");
 */

import { useEffect, useState } from "react";

export type Lang = "es" | "en";

const STORAGE_KEY = "wiki_lang";

export const DICTIONARY: Record<Lang, Record<string, string>> = {
  es: {
    // Common
    "common.cancel": "Cancelar",
    "common.save": "Guardar",
    "common.create": "Crear",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.search": "Buscar",
    "common.loading": "Cargando…",
    "common.error": "Error",
    "common.success": "Éxito",

    // Editor
    "editor.titlePlaceholder": "Título de la nota…",
    "editor.contentPlaceholder": "Comienza a escribir en Markdown o usa [[Nota]] para enlazar notas…",
    "editor.savedLocally": "Guardado Local",
    "editor.minutes": "min de lectura",
    "editor.word": "palabra",
    "editor.words": "palabras",
    "editor.wikiLink": "Wikilink",
    "editor.taskCreated": "Tarea creada",
    "editor.taskCreatedDesc": "Se añadió \"{title}\" a la lista.",

    // Dashboard
    "dashboard.welcome.morning": "Buenos días",
    "dashboard.welcome.afternoon": "Buenas tardes",
    "dashboard.welcome.evening": "Buenas noches",
    "dashboard.welcome.night": "Buenas noches",
    "dashboard.subtitle": "Aquí está lo que está pasando en tu workspace hoy.",
    "dashboard.continueTitle": "Continúa donde quedaste",
    "dashboard.lastEdited": "Última edición: {date}",

    // Quick actions
    "quick.newNote": "Nueva nota",
    "quick.newNoteDesc": "Crear documento en blanco",
    "quick.newMeeting": "Nueva reunión",
    "quick.newMeetingDesc": "Agendar evento en el calendario",
    "quick.newTask": "Nueva tarea",
    "quick.newTaskDesc": "Añadir tarea pendiente",

    // Graph
    "graph.searchPlaceholder": "Buscar nodo…",
    "graph.depthLevel": "Profundidad",
    "graph.allNodes": "Todo",
    "graph.center": "Centrar",
    "graph.zoomIn": "Acercar",
    "graph.zoomOut": "Alejar",
    "graph.fullscreen": "Pantalla completa",
    "graph.openDoc": "Abrir documento",
    "graph.empty": "Tu grafo está vacío",
    "graph.emptyDesc": "Crea tu primer documento para empezar a construir tu red de conocimiento. Las notas se conectan automáticamente con [[wiki-links]].",
    "graph.createFirst": "Crear primera nota",

    // File Explorer
    "fileExplorer.dragHandle": "Mover",
    "fileExplorer.renamed": "Renombrado",
    "fileExplorer.deleted": "Eliminado",
    "fileExplorer.moved": "Movido",
    "fileExplorer.movedDesc": "Se movió a \"{folder}\".",

    // Command Palette
    "cmd.search": "Buscar o ejecutar comando…",
    "cmd.recent": "Recientes",
    "cmd.actions": "Acciones",
    "cmd.files": "Notas",
    "cmd.noResults": "Sin resultados para \"{query}\"",
    "cmd.typeToSearch": "Escribe para buscar…",
    "cmd.createNote": "Crear nueva nota",
    "cmd.createNoteDesc": "Se creó una nueva nota sin título.",

    // Onboarding
    "onboarding.welcome.title": "¡Bienvenido a Arsenal Wiki!",
    "onboarding.welcome.subtitle": "Elige una opción para empezar",
    "onboarding.welcome.blank": "Empezar con nota en blanco",
    "onboarding.welcome.blankDesc": "Crea un documento vacío y escribe lo que quieras",
    "onboarding.welcome.template": "Usar una plantilla",
    "onboarding.welcome.templateDesc": "Estructuras prediseñadas: OKRs, RFC, retro, etc.",
    "onboarding.welcome.project": "Crear un proyecto",
    "onboarding.welcome.projectDesc": "Organiza tus notas en espacios temáticos",

    // Accessibility
    "a11y.skipToContent": "Saltar al contenido principal",
    "a11y.closeMenu": "Cerrar menú",
    "a11y.toggleTheme": "Alternar tema",

    "onboarding.step.workspace": "Workspace",
    "onboarding.step.document": "Documento",
    "onboarding.step.linking": "Vinculación",
    "onboarding.milestones": "hitos",
  },
  en: {
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.create": "Create",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.search": "Search",
    "common.loading": "Loading…",
    "common.error": "Error",
    "common.success": "Success",

    "editor.titlePlaceholder": "Note title…",
    "editor.contentPlaceholder": "Start writing in Markdown or use [[Note]] to link notes…",
    "editor.savedLocally": "Saved Locally",
    "editor.minutes": "min read",
    "editor.word": "word",
    "editor.words": "words",
    "editor.wikiLink": "Wikilink",
    "editor.taskCreated": "Task created",
    "editor.taskCreatedDesc": "\"{title}\" was added to the list.",

    "dashboard.welcome.morning": "Good morning",
    "dashboard.welcome.afternoon": "Good afternoon",
    "dashboard.welcome.evening": "Good evening",
    "dashboard.welcome.night": "Good night",
    "dashboard.subtitle": "Here's what's happening in your workspace today.",
    "dashboard.continueTitle": "Continue where you left off",
    "dashboard.lastEdited": "Last edited: {date}",

    "quick.newNote": "New note",
    "quick.newNoteDesc": "Create a blank document",
    "quick.newMeeting": "New meeting",
    "quick.newMeetingDesc": "Schedule a calendar event",
    "quick.newTask": "New task",
    "quick.newTaskDesc": "Add a pending task",

    "graph.searchPlaceholder": "Search node…",
    "graph.depthLevel": "Depth",
    "graph.allNodes": "All",
    "graph.center": "Center",
    "graph.zoomIn": "Zoom in",
    "graph.zoomOut": "Zoom out",
    "graph.fullscreen": "Fullscreen",
    "graph.openDoc": "Open document",
    "graph.empty": "Your graph is empty",
    "graph.emptyDesc": "Create your first document to start building your knowledge network. Notes connect automatically with [[wiki-links]].",
    "graph.createFirst": "Create first note",

    "fileExplorer.dragHandle": "Move",
    "fileExplorer.renamed": "Renamed",
    "fileExplorer.deleted": "Deleted",
    "fileExplorer.moved": "Moved",
    "fileExplorer.movedDesc": "Moved to \"{folder}\".",

    "cmd.search": "Search or run a command…",
    "cmd.recent": "Recent",
    "cmd.actions": "Actions",
    "cmd.files": "Notes",
    "cmd.noResults": "No results for \"{query}\"",
    "cmd.typeToSearch": "Type to search…",
    "cmd.createNote": "Create new note",
    "cmd.createNoteDesc": "A new untitled note was created.",

    "onboarding.welcome.title": "Welcome to Arsenal Wiki!",
    "onboarding.welcome.subtitle": "Choose an option to get started",
    "onboarding.welcome.blank": "Start with a blank note",
    "onboarding.welcome.blankDesc": "Create an empty document and write whatever you want",
    "onboarding.welcome.template": "Use a template",
    "onboarding.welcome.templateDesc": "Pre-designed structures: OKRs, RFC, retro, etc.",
    "onboarding.welcome.project": "Create a project",
    "onboarding.welcome.projectDesc": "Organize your notes in topical spaces",

    "a11y.skipToContent": "Skip to main content",
    "a11y.closeMenu": "Close menu",
    "a11y.toggleTheme": "Toggle theme",

    "onboarding.step.workspace": "Workspace",
    "onboarding.step.document": "Document",
    "onboarding.step.linking": "Linking",
    "onboarding.milestones": "milestones",
  },
};

/**
 * Devuelve el texto traducido para una key. Soporta placeholders `{name}`.
 * Fallback a la key misma si no existe (útil para desarrollo).
 */
export function t(key: string, lang: Lang = "es", params?: Record<string, string | number>): string {
  const dict = DICTIONARY[lang] ?? DICTIONARY.es;
  let template = dict[key] ?? key;
  if (params) {
    for (const [param, value] of Object.entries(params)) {
      template = template.replace(`{${param}}`, String(value));
    }
  }
  return template;
}

/**
 * Hook que devuelve la función `t` reactiva al idioma actual,
 * más el setter para cambiarlo.
 *
 * El idioma se persiste en localStorage.
 */
export function useI18n(): { lang: Lang; setLang: (l: Lang) => void; t: (key: string, params?: Record<string, string | number>) => string } {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "es";
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "es" || stored === "en") return stored;
    return "es";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  return {
    lang,
    setLang: setLangState,
    t: (key, params) => t(key, lang, params),
  };
}
